import { router, useLocalSearchParams } from "expo-router";
import { ThemedView } from "@/components/ThemedView";
import { ActivityIndicator, KeyboardAvoidingView, StyleSheet, View, TouchableOpacity, Text, Linking, Platform } from "react-native";
import { useState, useEffect } from "react";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedInput } from "@/components/ThemedInput";
import ErrorBoundary2 from "@/components/ErrorBoundary";
import { useFirebase } from "@/components/firebase/useFirebase";
import { signInWithCustomToken, signInWithEmailAndPassword, getAdditionalUserInfo, isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { CustomAlert } from "@/components/ThemedAlert";
import { sendForgotPasswordEmail, registerInvitedUser, loginWithCredentials } from "../services/firebase/user-functions";
import { FontAwesome } from "@expo/vector-icons";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedText } from "@/components/ThemedText";
import { validatePasswordPolicy } from "common247";
import { useAuth } from "@components/auth/useAuth";
import { getPrivacyPolicyUrl, getAboutUrl } from "@/services/environment-urls";

// See: https://docs.expo.dev/router/advanced/authentication/
// And: https://galaxies.dev/react-native-firebase-authentication-expo-router
export default function SignIn() {
  const { email: queryEmail, token, reset, id } = useLocalSearchParams<{ email?: string; token?: string; reset?: string; id?: string }>();
  const [email, setEmail] = useState(queryEmail || "");
  const [password, setPassword] = useState("");
  const [authenticatingToken, setAuthenticatingToken] = useState(false);
  const [authenticatingLink, setAuthenticatingLink] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [confirmPassword, setConfirmPassword] = useState("");
  const [registering, setRegistering] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const { signInWithGoogle } = useAuth();
  const services = useFirebase();

  useEffect(() => {
    if (!token || !services?.auth) return;

    const authenticateWithToken = async () => {
      setAuthenticatingToken(true);
      try {
        console.log("Authenticating with custom token...");
        await signInWithCustomToken(services.auth, token);
        console.log("Custom token authentication success.");
        if (reset === "true") {
          router.replace({ pathname: "/(tabs)/profile" as any, params: { resetPassword: "true" } });
        } else {
          router.replace("/");
        }
      } catch (err: any) {
        console.error("Custom token login error:", err);
        CustomAlert.alert("Error", err.message || "Failed to log in with the invitation link.");
      } finally {
        setAuthenticatingToken(false);
      }
    };

    authenticateWithToken();
  }, [token, services, reset]);

  /**
   * Effect to check if the current page load is a Firebase passwordless email sign-in link.
   * If it is, completes the sign-in using the user's email address.
   */
  useEffect(() => {
    if (!services?.auth) return;
    if (typeof window === "undefined" || !window.location) return;

    if (isSignInWithEmailLink(services.auth, window.location.href)) {
      const authenticateWithEmailLink = async () => {
        setAuthenticatingLink(true);
        try {
          let savedEmail = window.localStorage.getItem("emailForSignIn") || queryEmail;
          if (!savedEmail) {
            savedEmail = window.prompt("Please provide your email for confirmation") || "";
          }
          if (!savedEmail) {
            console.error("No email provided for sign-in confirmation.");
            return;
          }

          console.log("Signing in with email link...");
          const result = await signInWithEmailLink(services.auth, savedEmail, window.location.href);
          window.localStorage.removeItem("emailForSignIn");
          const info = getAdditionalUserInfo(result);
          console.log("Link Sign-In Success. User Info:", info);
          router.replace("/");
        } catch (err: any) {
          console.error("Error signing in with link:", err);
          CustomAlert.alert("Authentication Failed", err.message || "Failed to sign in with email link.");
        } finally {
          setAuthenticatingLink(false);
        }
      };

      authenticateWithEmailLink();
    }
  }, [services, queryEmail]);

  /**
   * Submits the registration password for the invited user,
   * validates matching passwords, performs password policy check,
   * calls the backend to register the user, and signs them in.
   */
  const handleRegisterSubmit = async () => {
    setValidationError("");
    if (!id) {
      setValidationError("Missing invitation ID.");
      return;
    }
    if (!password) {
      setValidationError("Password is required.");
      return;
    }
    if (password !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return;
    }

    // Validate password policy locally
    const policyResult = validatePasswordPolicy(password);
    if (!policyResult.isValid) {
      setValidationError(policyResult.errorMessage || "Password does not meet requirements.");
      return;
    }

    if (!services) {
      setValidationError("Firebase services are not available.");
      return;
    }

    setRegistering(true);
    try {
      const result = await registerInvitedUser(services, { id, password });
      if (!result.status) {
        setValidationError(result.error || "Failed to register user.");
        return;
      }

      if (result.token) {
        // Log in with the returned custom token
        await signInWithCustomToken(services.auth, result.token);
      } else {
        // Fallback: log in directly with email & password since the user just set it
        const targetEmail = (queryEmail || email).trim();
        await signInWithEmailAndPassword(services.auth, targetEmail, password);
      }
      router.replace("/");
    } catch (err: any) {
      console.error("Register user error:", err);
      setValidationError(err.message || "An unexpected error occurred.");
    } finally {
      setRegistering(false);
    }
  };

  const handleForgotPassword = async () => {
    setEmailError("");
    const emailClean = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailClean)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not available.");
      return;
    }

    setSendingReset(true);
    try {
      const currentDomain = typeof window !== "undefined" && window.location ? window.location.host : "memories-dev-247.web.app";
      const response = await sendForgotPasswordEmail(services, {
        email: email.trim(),
        domain: currentDomain,
      });

      if (response.status) {
        CustomAlert.alert("Check Your Email", "If the email is registered, we have sent a secure link to reset your password.");
      } else {
        CustomAlert.alert("Error", response.error || "Failed to request password reset.");
      }
    } catch (err: any) {
      console.error("Forgot password error:", err);
      CustomAlert.alert("Error", err.message || "An unexpected error occurred.");
    } finally {
      setSendingReset(false);
    }
  };

  /**
   * Opens the publicly accessible Privacy Policy web page.
   */
  const handleOpenPrivacyPolicy = async () => {
    const url = getPrivacyPolicyUrl();

    try {
      await Linking.openURL(url);
    } catch (err) {
      console.error("Failed to open Privacy Policy URL:", err);
      CustomAlert.alert("Privacy Policy", `Please visit: ${url}`);
    }
  };

  /**
   * Opens the publicly accessible About Memories web page.
   */
  const handleOpenAbout = async () => {
    const url = getAboutUrl();

    try {
      await Linking.openURL(url);
    } catch (err) {
      console.error("Failed to open About URL:", err);
      CustomAlert.alert("About Memories", `Please visit: ${url}`);
    }
  };

  console.log("SignIn", authenticatingToken, authenticatingLink);

  const [showEmailAuth, setShowEmailAuth] = useState(!!queryEmail || !!token);

  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const textMuted = useThemeColor({}, "textMuted");
  const buttonBg = useThemeColor({}, "buttonSecondary");
  const activeTint = useThemeColor({}, "tint");

  const handleGoogleSignIn = async () => {
    try {
      console.warn("Performing google signin!");
      setIsLoggingIn(true);
      const user = await signInWithGoogle();
      if (user) {
        router.replace("/");
      }
    } catch (err: any) {
      if (
        err?.code === "auth/popup-closed-by-user" ||
        err?.code === "auth/cancelled-popup-request" ||
        err?.code === "12501" ||
        String(err?.message || "").toLowerCase().includes("cancelled")
      ) {
        console.info("[SignIn] User dismissed Google sign-in.");
        return;
      }
      if (
        err?.code === "10" ||
        String(err?.code).includes("DEVELOPER_ERROR") ||
        String(err?.message || "").includes("DEVELOPER_ERROR")
      ) {
        console.warn(
          "[SignIn] Google Sign-In DEVELOPER_ERROR (code 10): Ensure Android SHA-1 fingerprint and Web Client ID are registered in Firebase Console.",
          err
        );
        CustomAlert.alert(
          "Google Sign-In Configuration",
          "Google Sign-In configuration mismatch (code 10: DEVELOPER_ERROR). Please check Android SHA-1 fingerprint in Firebase Console."
        );
        return;
      }
      console.error("Google login error:", err);
      CustomAlert.alert("Authentication Failed", err.message || "Failed to log in with Google.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (authenticatingToken || authenticatingLink) {
    return (
      <ThemedView style={styles.container}>
        <ActivityIndicator size="large" color="#059669" />
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.container}>
      <KeyboardAvoidingView behavior="padding" style={styles.card}>
        <ErrorBoundary2>
          <View style={{ position: "relative" }}>
            <ThemedText type="editorialTitle" style={styles.titleText}>
              Memories
            </ThemedText>
            <ThemedText style={styles.subtitleText}>Sign in to your long term, always on, memory storage</ThemedText>
            <ThemedText style={styles.subtitleText}>Memories are by-invite only, so if you do not have an account, ask a friend or family member who is already using the service to invite you!</ThemedText>
          </View>

          {sendingReset || registering ? (
            <ActivityIndicator size="large" color={activeTint} style={{ margin: 40 }} />
          ) : reset === "true" && id ? (
            <View
              style={styles.emailContainer}
              role="form"
              accessibilityRole="form"
              {...(Platform.OS === "web" ? { onSubmit: (e: any) => e.preventDefault() } : {})}
            >
              <ThemedText type="subtitle" style={styles.formTitle}>
                Activate Your Account
              </ThemedText>
              <ThemedText style={styles.formSubtitle}>
                Welcome! Continue with Google to instantly link and activate your account for {queryEmail || email}, or set a password below.
              </ThemedText>

              <View style={[styles.oauthContainer, { marginTop: 12, marginBottom: 16 }]}>
                <TouchableOpacity
                  testID="invited-google-button"
                  onPress={handleGoogleSignIn}
                  disabled={isLoggingIn}
                  activeOpacity={0.85}
                  style={[
                    styles.oauthButton,
                    { backgroundColor: buttonBg, borderColor, opacity: isLoggingIn ? 0.7 : 1 },
                  ]}
                >
                  {isLoggingIn ? (
                    <ActivityIndicator size="small" color={textColor} style={styles.oauthIcon} />
                  ) : (
                    <FontAwesome name="google" size={20} color={textColor} style={styles.oauthIcon} />
                  )}
                  <Text style={[styles.oauthText, { color: textColor }]}>
                    {isLoggingIn ? "Signing in..." : "Continue with Google"}
                  </Text>
                </TouchableOpacity>

                <View style={styles.dividerContainer}>
                  <View style={[styles.dividerLine, { backgroundColor: borderColor }]} />
                  <Text style={[styles.dividerText, { color: textMuted }]}>or set your password</Text>
                  <View style={[styles.dividerLine, { backgroundColor: borderColor }]} />
                </View>
              </View>

              <ThemedText type="fieldLabel">New Password</ThemedText>
              <ThemedInput
                value={password}
                onChangeText={(text) => {
                  setPassword(text);
                  setValidationError("");
                }}
                secureTextEntry
                placeholder="New Password"
                autoComplete="new-password"
                textContentType="newPassword"
              />

              <ThemedText type="fieldLabel" style={{ marginTop: 8 }}>Confirm Password</ThemedText>
              <ThemedInput
                value={confirmPassword}
                onChangeText={(text) => {
                  setConfirmPassword(text);
                  setValidationError("");
                }}
                secureTextEntry
                placeholder="Confirm Password"
                autoComplete="new-password"
                textContentType="newPassword"
                onSubmitEditing={handleRegisterSubmit}
              />

              {validationError ? <Text style={styles.errorText}>{validationError}</Text> : null}

              <View style={styles.buttonContainer}>
                <ThemedButton onPress={handleRegisterSubmit} title="Create Account & Sign In" />
              </View>
            </View>
          ) : (
            <View style={styles.oauthContainer}>
              <TouchableOpacity
                testID="continue-with-google-button"
                onPress={handleGoogleSignIn}
                disabled={isLoggingIn}
                activeOpacity={0.85}
                style={[
                  styles.oauthButton,
                  { backgroundColor: buttonBg, borderColor, opacity: isLoggingIn ? 0.7 : 1 },
                ]}
              >
                {isLoggingIn ? (
                  <ActivityIndicator size="small" color={textColor} style={styles.oauthIcon} />
                ) : (
                  <FontAwesome name="google" size={20} color={textColor} style={styles.oauthIcon} />
                )}
                <Text style={[styles.oauthText, { color: textColor }]}>
                  {isLoggingIn ? "Signing in..." : "Continue with Google"}
                </Text>
              </TouchableOpacity>

              <View style={styles.dividerContainer}>
                <View style={[styles.dividerLine, { backgroundColor: borderColor }]} />
                <Text style={[styles.dividerText, { color: textMuted }]}>or use username / email</Text>
                <View style={[styles.dividerLine, { backgroundColor: borderColor }]} />
              </View>

              {!showEmailAuth ? (
                <TouchableOpacity onPress={() => setShowEmailAuth(true)} activeOpacity={0.85} style={[styles.toggleButton, { borderColor }]}>
                  <Text style={[styles.toggleText, { color: textColor }]}>Sign in with Username or Email</Text>
                </TouchableOpacity>
              ) : (
                <View
                  style={styles.emailContainer}
                  role="form"
                  accessibilityRole="form"
                  {...(Platform.OS === "web" ? { onSubmit: (e: any) => e.preventDefault() } : {})}
                >
                  <ThemedInput
                    value={email}
                    onChangeText={(val) => {
                      setEmail(val);
                      if (emailError) setEmailError("");
                    }}
                    autoCapitalize="none"
                    placeholder="Username or Email Address"
                    error={emailError}
                    autoComplete="username"
                    textContentType="username"
                  />
                  <ThemedInput
                    value={password}
                    onChangeText={(val) => {
                      setPassword(val);
                      if (passwordError) setPasswordError("");
                    }}
                    secureTextEntry
                    placeholder="Password"
                    error={passwordError}
                    autoComplete="current-password"
                    textContentType="password"
                  />

                  <View style={styles.buttonContainer}>
                    <ThemedButton
                      onPress={async () => {
                        let hasError = false;
                        if (!email || !email.trim()) {
                          setEmailError("Username or email address is required.");
                          hasError = true;
                        }
                        if (!password) {
                          setPasswordError("Password is required.");
                          hasError = true;
                        }
                        if (hasError) return;

                        if (!services) {
                          CustomAlert.alert("Error", "Firebase services are not available.");
                          return;
                        }

                        const cleanIdentifier = email.trim();
                        const isEmail = cleanIdentifier.includes("@");

                        setIsLoggingIn(true);
                        try {
                          let loginEmail = cleanIdentifier;

                          if (isEmail) {
                            await signInWithEmailAndPassword(services.auth, loginEmail, password);
                            router.replace("/");
                            return;
                          }

                          // Username provided: resolve email and authenticate credentials via backend function
                          const res = await loginWithCredentials(services, {
                            identifier: cleanIdentifier,
                            password,
                          });

                          if (!res.status) {
                            CustomAlert.alert("Login Failed", res.error || "Invalid username or password.");
                            return;
                          }

                          // If token returned and usable, sign in with custom token
                          if (res.token) {
                            try {
                              await signInWithCustomToken(services.auth, res.token);
                              router.replace("/");
                              return;
                            } catch (tokenSignErr) {
                              console.warn("signInWithCustomToken failed, falling back to signInWithEmailAndPassword:", tokenSignErr);
                            }
                          }

                          // Fallback to signInWithEmailAndPassword using resolved targetEmail
                          if (res.email) {
                            await signInWithEmailAndPassword(services.auth, res.email, password);
                            router.replace("/");
                            return;
                          }

                          CustomAlert.alert("Login Failed", "Could not authenticate with provided credentials.");
                        } catch (err: any) {
                          console.error("Login error:", err);
                          let errorMsg = err.message || "Failed to log in.";
                          if (
                            err.code === "auth/invalid-credential" ||
                            err.code === "auth/wrong-password" ||
                            err.code === "auth/user-not-found" ||
                            err.code === "auth/invalid-email"
                          ) {
                            errorMsg = "Invalid username or password.";
                          }
                          CustomAlert.alert("Login Failed", errorMsg);
                        } finally {
                          setIsLoggingIn(false);
                        }
                      }}
                      title="Login"
                      loading={isLoggingIn}
                    />
                    <ThemedButton onPress={handleForgotPassword} title="Forgot Password" variant="minimal" />
                  </View>
                </View>
              )}

              <View
                style={[
                  styles.disclaimerContainer,
                  { borderColor: borderColor, backgroundColor: buttonBg },
                ]}
                accessibilityRole="summary"
                accessibilityLabel="Analytics Disclaimer"
              >
                <ThemedText style={[styles.disclaimerTitle, { color: textColor }]}>
                  Notice
                </ThemedText>
                <ThemedText style={[styles.disclaimerText, { color: textMuted }]}>
                  This platform tracks user activity via Google Analytics to provide better user experience and quicker issue resolution. If this is not acceptable, please do not use the platform.
                </ThemedText>
              </View>

              <View style={styles.footerLinksContainer}>
                <TouchableOpacity
                  accessibilityRole="link"
                  accessibilityLabel="About Memories"
                  onPress={handleOpenAbout}
                  style={styles.footerLinkButton}
                >
                  <Text style={styles.footerLinkText}>About</Text>
                </TouchableOpacity>

                <Text style={styles.footerLinkDivider}>•</Text>

                <TouchableOpacity
                  accessibilityRole="link"
                  accessibilityLabel="Privacy Policy"
                  onPress={handleOpenPrivacyPolicy}
                  style={styles.footerLinkButton}
                >
                  <Text style={styles.footerLinkText}>Privacy Policy</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
        </ErrorBoundary2>
      </KeyboardAvoidingView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  card: {
    width: "100%",
    maxWidth: 340,
    padding: 24,
    borderRadius: 0,
  },
  titleText: {
    textAlign: "center",
    marginBottom: 6,
    fontSize: 32,
  },
  subtitleText: {
    textAlign: "center",
    fontSize: 13,
    color: "#78716C",
    marginBottom: 20,
    lineHeight: 18,
  },
  oauthContainer: {
    width: "100%",
  },
  oauthButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 0,
    borderWidth: 1,
    marginVertical: 5,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 3,
    elevation: 1,
  },
  oauthIcon: {
    marginRight: 12,
  },
  oauthText: {
    fontSize: 15,
    fontWeight: "600",
    letterSpacing: 0.15,
  },
  dividerContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 18,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    marginHorizontal: 12,
    fontSize: 13,
    fontWeight: "500",
  },
  toggleButton: {
    borderWidth: 1,
    borderRadius: 0,
    paddingVertical: 12,
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 5,
  },
  toggleText: {
    fontSize: 15,
    fontWeight: "600",
  },
  emailContainer: {
    width: "100%",
  },
  buttonContainer: {
    marginTop: 12,
    gap: 8,
  },
  formTitle: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
  },
  formSubtitle: {
    fontSize: 14,
    color: "#64748B",
    textAlign: "center",
    marginBottom: 16,
  },
  errorText: {
    color: "#DC2626",
    fontSize: 14,
    marginTop: 4,
    textAlign: "center",
  },
  footerLinksContainer: {
    marginTop: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  footerLinkButton: {
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  footerLinkText: {
    fontSize: 13,
    color: "#64748B",
    textDecorationLine: "underline",
    fontWeight: "500",
  },
  footerLinkDivider: {
    fontSize: 14,
    color: "#94A3B8",
  },
  disclaimerContainer: {
    marginTop: 18,
    padding: 12,
    borderWidth: 1,
    borderRadius: 0,
  },
  disclaimerTitle: {
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  disclaimerText: {
    fontSize: 12,
    lineHeight: 16,
  },
});
